import styled, { createGlobalStyle, css } from 'styled-components'
import { color } from 'ui/themes/utils'

import Skeleton from 'ui/blocks/Skeleton'
import FocalImage from 'ui/blocks/FocalImage'

export const HostStyles = createGlobalStyle`
  :host {
    --lines-empty-text-color: var(--color-primary-foreground, ${color('Text/Primary')});

    --line-border: 1px solid var(--color-border, ${color('Misc/Divider')});

    --line-image-size: var(--cart-line-image-size, 60px);
    --line-image-border-radius: var(--cart-line-image-border-radius, 6px);
    --line-image-border: 1px solid var(--color-border, ${color('Misc/Divider')});

    --line-icon-color: var(--color-primary-foreground, #0c2747);
    --line-control-border-radius: var(--cart-line-control-border-radius, 6px);
    --line-control-border: 1px solid var(--color-border, ${color('Misc/Divider')});
    --line-input-color: var(--color-primary-foreground, #0c2747);

    --line-availability-border: 1px solid;
    --line-availability-border-color-available: var(--color-green, ${color('Success/Base')});
    --line-availability-border-color-unavailable: var(--color-red, ${color('Danger/Base')});
    --line-availability-color-available: var(--color-green, ${color('Success/Base')});
    --line-availability-color-unavailable: var(--color-red, ${color('Danger/Base')});
    --line-availability-background-color-available: transparent;
    --line-availability-background-color-unavailable: transparent;

    --line-title-color: var(--color-primary-foreground, ${color('Text/Primary')});
    --line-title-font-size: 16px;

    --line-price-color: var(--color-primary-foreground, ${color('Text/Primary')});
    --line-price-font-size: 16px;

    --line-variations-color: var(--color-primary-foreground-lightened, ${color('Text/Secondary')});
    --line-variations-font-size: 14px;

    --line-toggle-color: var(--color-primary-foreground, ${color('branding')});
    --line-toggle-font-size: 16px;

    --line-bundle-item-quantity-color: var(--color-primary-foreground, ${color('Text/Secondary')});
    --line-bundle-item-quantity-font-size: 16px;
    --line-bundle-item-quantity-background: var(--color-primary-background, ${color('white')});

    --line-bundle-item-name-color: var(--color-primary-foreground, ${color('Text/Primary')});
    --line-bundle-item-name-font-size: 16px;
  }
`

export const Lines = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content max-content;

  border-top: var(--line-border);

  * {
    box-sizing: border-box;
  }

  ${FocalImage} {
    width: var(--line-image-size);
    height: var(--line-image-size);

    border-radius: var(--line-image-border-radius);
    border: var(--line-image-border);
  }
`

export const EmptyText = styled.h3`
  display: inline-block;

  color: var(--lines-empty-text-color);
`

export const Cell = styled.div`
  padding: 20px 12px;

  border-bottom: var(--line-border);
`

export const Image = styled(Cell)`
  grid-column: 1 / 2;

  padding-left: 0;

  @media screen and (max-width: 576px) {
    border: none;

    padding-bottom: 0;
  }
`

export const Info = styled(Cell)`
  grid-column: 2 / 3;

  @media screen and (max-width: 576px) {
    border: none;

    padding-bottom: 0;
  }
`

export const Availability = styled.span`
  display: inline-block;

  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;

  margin: 8px 0;
  padding: 2px 8px;

  border: var(--line-availability-border);
  border-radius: 20px;

  ${({ available }) =>
    available ?
      css`
          border-color: var(--line-availability-border-color-available);
          color: var(--line-availability-color-available);
          background-color: var(--line-availability-background-color-available);
        ` :
      css`
          border-color: var(--line-availability-border-color-unavailable);
          color: var(--line-availability-color-unavailable);
          background-color: var(--line-availability-background-color-unavailable);
        `}
`

export const Controls = styled(Cell)`
  display: flex;

  gap: 4px;

  grid-column: 3 / 4;

  padding-right: 0;

  @media screen and (max-width: 576px) {
    grid-column: 1 / 3;
  }

  ${({ withRightOffset }) => withRightOffset && css`
    padding-right: 12px;
  `}
`

export const ActionButton = styled.button`
  border: none;
  background: none;

  width: 40px;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  margin-right: 8px;

  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;

    fill: var(--line-icon-color);
    color: var(--line-icon-color);
  }
`

export const QuantityButton = styled(ActionButton)`
  border: var(--line-control-border);
  border-radius: var(--line-control-border-radius);

  background-color: var(--line-bundle-item-quantity-background);

  margin-right: 0;

  svg {
    width: 14px;
    height: 14px;

    fill: var(--line-icon-color);
    color: var(--line-icon-color);
  }
`

export const QuantityInput = styled.input`
  border: none;
  border: var(--line-control-border);

  background-color: var(--line-bundle-item-quantity-background);

  color: var(--line-input-color);

  border-radius: var(--line-control-border-radius);

  font-size: 14px;
  line-height: 20px;

  text-align: center;

  min-height: 40px;
  max-height: 40px;
  width: 60px;
`

export const Price = styled(Cell)`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  padding-top: 30px;
  padding-right: 0;

  font-family: var(--font-body);
  font-size: var(--line-price-font-size);
  color: var(--line-price-color);

  ${Skeleton} {
    display: block;
  }

  grid-column: 4 / 5;

  @media screen and (max-width: 576px) {
    grid-column: 3 / 4;
  }
`

export const Title = styled.h3`
  font-family: var(--font-heading);
  font-size: 16px;

  font-size: var(--line-title-font-size);
  color: var(--line-title-color);

  margin: 0;
`

export const Variations = styled.p`
  font-family: var(--font-body);
  font-size: var(--line-variations-font-size);

  color: var(--line-variations-color);

  margin: 0;
`

export const Contents = styled.div``
